import { Routes } from "@angular/router";
import { authGuard } from "./core/guards/auth.guard";
import { roleGuard } from "./core/guards/role.guard";
import { permissionGuard } from "./core/guards/permission.guard";
import { platformRedirectGuard } from "./core/guards/platform-redirect.guard";

export const appRoutes: Routes = [
  {
    path: "",
    loadComponent: () =>
      import("./features/dashboard/dashboard.page").then(
        (m) => m.DashboardPage
      ),
    canActivate: [authGuard],
  },
  {
    path: "auth",
    loadChildren: () =>
      import("./features/auth/routes").then((m) => m.AUTH_ROUTES),
  },
  {
    path: "service-one",
    loadChildren: () =>
      import("./features/service-one/routes").then((m) => m.SERVICE_ONE_ROUTES),
    canActivate: [authGuard],
  },
  {
    path: "service-two",
    loadComponent: () =>
      import("./features/service-two/service-two.page").then(
        (m) => m.ServiceTwoPage
      ),
    canActivate: [authGuard],
  },
  {
    path: "service-three",
    loadChildren: () =>
      import("./features/service-three/routes").then(
        (m) => m.SERVICE_THREE_ROUTES
      ),
    canActivate: [authGuard],
  },
  {
    path: "service-four",
    loadComponent: () =>
      import("./features/gateway/gateway-status.page").then(
        (m) => m.GatewayStatusPage
      ),
    canActivate: [authGuard],
  },
  {
    path: "admin",
    loadChildren: () =>
      import("./features/admin/routes").then((m) => m.ADMIN_ROUTES),
    canActivate: [authGuard, roleGuard],
    data: { roles: ["admin"] },
  },
  {
    path: "users",
    loadChildren: () =>
      import("./features/users/routes").then((m) => m.USER_ROUTES),
    canActivate: [authGuard, permissionGuard],
    data: { permissions: ["users:read"] },
  },
  {
    path: "contracts",
    loadComponent: () =>
      import("./features/users/pages/contracts.page").then(
        (m) => m.ContractsPage
      ),
    canActivate: [authGuard, roleGuard],
    data: { roles: ["super_admin"] },
  },
  {
    path: "admin/users",
    loadComponent: () =>
      import("./features/service-two/service-two.page").then(
        (m) => m.ServiceTwoPage
      ),
    canActivate: [authGuard, roleGuard],
    data: { roles: ["admin"] },
  },
  {
    path: "profile",
    loadComponent: () =>
      import("./features/users/user-profile.page").then(
        (m) => m.UserProfilePage
      ),
    canActivate: [authGuard],
  },
  {
    path: "security",
    loadComponent: () =>
      import("./features/users/user-statistics.page").then(
        (m) => m.UserStatisticsPage
      ),
    canActivate: [authGuard],
  },
  {
    path: "tasks",
    loadChildren: () =>
      import("./features/tasks/routes").then((m) => m.TASK_ROUTES),
    canActivate: [authGuard, permissionGuard],
    data: { permissions: ["tasks:read"] },
  },
  {
    path: "attendances",
    loadChildren: () =>
      import("./features/attendances/routes").then((m) => m.ATTENDANCE_ROUTES),
    canActivate: [authGuard, permissionGuard],
    data: { permissions: ["attendance:read"] },
  },
  {
    path: "gateway",
    loadChildren: () =>
      import("./features/gateway/routes").then((m) => m.GATEWAY_ROUTES),
    canActivate: [authGuard],
  },
  {
    path: "reports",
    loadChildren: () =>
      import("./features/reports/routes").then((m) => m.REPORTS_ROUTES),
    canActivate: [authGuard, permissionGuard],
    data: { permissions: ["reports:read"] },
  },
  {
    path: "projects",
    loadChildren: () =>
      import("./features/projects/routes").then((m) => m.PROJECT_ROUTES),
    canActivate: [authGuard, permissionGuard],
    data: { permissions: ["projects:read"] },
  },
  {
    path: "my-department",
    loadComponent: () =>
      import("./features/departments/pages/my-department.page").then(
        (m) => m.MyDepartmentPage
      ),
    canActivate: [authGuard, permissionGuard],
    data: { permissions: ["departments:read"] },
  },
  {
    path: "departments",
    loadChildren: () =>
      import("./features/departments/routes").then((m) => m.DEPARTMENTS_ROUTES),
    canActivate: [authGuard, permissionGuard],
    data: { permissions: ["departments:read"] },
  },
  {
    path: "rbac",
    loadChildren: () =>
      import("./features/rbac/routes").then((m) => m.RBAC_ROUTES),
    canActivate: [authGuard, permissionGuard],
    data: { permissions: ["roles:read"] },
  },
  {
    path: "monitoring",
    loadChildren: () =>
      import("./features/monitoring/routes").then((m) => m.MONITORING_ROUTES),
    canActivate: [authGuard, permissionGuard],
    data: { permissions: ["monitoring:read"] },
  },
  {
    path: "ai",
    loadChildren: () => import("./features/ai/routes").then((m) => m.AI_ROUTES),
    canActivate: [authGuard, permissionGuard],
    data: { permissions: ["tasks:read"] },
  },
  {
    path: "audit",
    loadChildren: () =>
      import("./features/audit/routes").then((m) => m.AUDIT_ROUTES),
    canActivate: [authGuard, permissionGuard],
    data: { permissions: ["audit:read"] },
  },
  {
    path: "notifications",
    loadChildren: () =>
      import("./features/notifications/routes").then(
        (m) => m.NOTIFICATIONS_ROUTES
      ),
    canActivate: [authGuard, permissionGuard],
    data: { permissions: ["notifications:read"] },
  },
  // NOTE: Settings must be accessible even for users without `platform:read`.
  // The `platform` feature root is protected by `platform:read`, which would otherwise
  // redirect non-platform users to `/`.
  {
    path: "platform/settings",
    loadComponent: () =>
      import("./features/platform/pages/settings.page").then((m) => m.SettingsPage),
    canActivate: [authGuard],
  },
  {
    path: "platform",
    loadChildren: () =>
      import("./features/platform/routes").then((m) => m.PLATFORM_ROUTES),
    canActivate: [authGuard, permissionGuard],
    data: { permissions: ["platform:read"] },
  },
  {
    path: "calendar",
    loadChildren: () =>
      import("./features/calendar/routes").then((m) => m.CALENDAR_ROUTES),
    canActivate: [authGuard],
  },
  {
    path: "messenger",
    loadChildren: () =>
      import("./features/messenger/routes").then((m) => m.MESSENGER_ROUTES),
    canActivate: [authGuard],
  },
  {
    path: "feed",
    loadChildren: () =>
      import("./features/feed/routes").then((m) => m.FEED_ROUTES),
    canActivate: [authGuard],
  },
  {
    path: "drive",
    loadChildren: () =>
      import("./features/drive/routes").then((m) => m.DRIVE_ROUTES),
    canActivate: [authGuard],
  },
  {
    path: "favorites",
    loadChildren: () =>
      import("./features/favorites/routes").then((m) => m.FAVORITES_ROUTES),
    canActivate: [authGuard],
  },
  {
    path: "help",
    loadChildren: () =>
      import("./features/help/routes").then((m) => m.HELP_ROUTES),
    canActivate: [authGuard],
  },
  // CRM Routes
  {
    path: "crm",
    loadChildren: () =>
      import("./features/crm/routes").then((m) => m.CRM_ROUTES),
    canActivate: [authGuard, roleGuard],
    data: { roles: ["CRM_MANAGER", "CRM_STAFF", "admin", "super_admin"] },
  },
  // Static CRM Reports Route
  {
    path: "crm-reports",
    loadComponent: () =>
      import("./features/crm/pages/crm-reports.page").then(
        (m) => m.CrmReportsPage
      ),
    canActivate: [authGuard],
  },
  // Academic Routes
  {
    path: "academic",
    loadChildren: () =>
      import("./features/academic/routes").then((m) => m.ACADEMIC_ROUTES),
    canActivate: [authGuard, roleGuard],
    data: {
      roles: [
        "academic_head",
        "academic_manager",
        "faculty",
        "student",
        "admin",
        "super_admin",
      ],
    },
  },
  {
    path: "**",
    redirectTo: "",
  },
];
